import { browser } from '$app/environment';
import { isFeatureEnabled } from '@core/product';
import { FeatureFlag } from '@core/product/flags';
import type { UserProfile } from '@domain/Profile';
import type { ErrorCodeMessage, Result } from '@shared/results';
import type { Auth, User, UserCredential } from 'firebase/auth';

// Reference
// https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth

// Firebase auth and SDK functions are loaded dynamically to avoid SSR crashes.
// firebase/auth and init.client touch browser globals (window, document) at import time.
// With noExternal: true in Vite SSR config, static imports would bundle and execute them server-side.
// Dynamic import() behind `browser` checks is the officially recommended pattern (SvelteKit FAQ, Firebase docs).

const debugLog = () => isFeatureEnabled(FeatureFlag.DebugAuth);

let _auth: Auth;
let _firebaseAuth: typeof import('firebase/auth');

export interface SignInResult extends Result {
  user?: User;
}

export interface VerifyPasswordResetResult extends Result {
  email?: string;
}

export interface AuthInitState {
  loading: boolean;
  ready: boolean;
}

export interface AuthState extends AuthInitState {
  user: User | undefined;
  profile: UserProfile | undefined;
}

export const authInit = $state<AuthInitState>({
  loading: browser, // SSR: false (no Firebase to wait for). Browser: true (waiting for Firebase init).
  ready: !browser, // SSR: true (render immediately). Browser: false (not ready until onAuthStateChanged fires).
});

// Module-level cache of the `handle` and `handleDisplay` custom claims.
// Populated by `refreshHandleClaim()` (typically called from the layout's
// onAuthChange listener and after a session refresh in the welcome flow).
// The token claims aren't synchronously readable from the User object, so
// we cache the latest known values here and expose them via getAuthState().
const handleCache = $state<{ value: string | undefined; display: string | undefined }>({
  value: undefined,
  display: undefined,
});

// Dynamic init: load Firebase auth + client app, wire up onAuthStateChanged.
const _initPromise: Promise<void> = browser
  ? Promise.all([import('firebase/auth'), import('../init.client')]).then(([firebaseAuth, client]) => {
      _firebaseAuth = firebaseAuth;
      _auth = client.auth;

      const unsubscribe = firebaseAuth.onAuthStateChanged(_auth, (user) => {
        if (debugLog()) console.debug('auth.svelte|onAuthStateChanged|user', user);
        if (authInit.ready) return;
        if (debugLog()) console.debug('auth.svelte|onAuthStateChanged|ready=true');
        authInit.ready = true;
        authInit.loading = false;
        unsubscribe();
      });
    })
  : Promise.resolve();

// Resolved once Firebase auth reports its initial state (browser-only).
// During SSR, resolves immediately. Does NOT return the user — use getCurrentUser() after awaiting.
export const authReady: Promise<void> = new Promise((resolve) => {
  if (!browser) {
    resolve();
    return;
  }
  _initPromise.then(() => {
    _auth.authStateReady().then(() => {
      resolve();
    });
  });
});

export function getAuthState(): AuthState {
  if (!browser || !_auth) {
    return { user: undefined, profile: undefined, loading: authInit.loading, ready: authInit.ready };
  }
  const user = _auth.currentUser;
  if (debugLog()) console.debug('auth.svelte|getAuthState|user', user);
  return {
    user: user ?? undefined,
    profile: toUserProfile(user),
    loading: authInit.loading,
    ready: authInit.ready,
  };
}

/**
 * Refreshes the cached `handle` custom claim from the user's current ID
 * token. Pass `null`/`undefined` to clear (e.g. on sign-out). Call this
 * after sign-in, after `refreshSession` succeeds, and from the layout's
 * onAuthChange listener so the cache stays in sync.
 */
export async function refreshHandleClaim(user: User | undefined | null): Promise<void> {
  if (!user) {
    handleCache.value = undefined;
    handleCache.display = undefined;
    return;
  }
  try {
    const tokenResult = await user.getIdTokenResult();
    const claim = tokenResult.claims['handle'];
    const displayClaim = tokenResult.claims['handleDisplay'];
    handleCache.value = typeof claim === 'string' && claim.trim() ? claim : undefined;
    handleCache.display = typeof displayClaim === 'string' && displayClaim.trim() ? displayClaim : undefined;
  } catch (err) {
    console.error('auth.svelte|refreshHandleClaim|error', err);
    handleCache.value = undefined;
    handleCache.display = undefined;
  }
}

export async function register(email: string, password: string) {
  return await toSignInResult(_firebaseAuth.createUserWithEmailAndPassword(_auth, email, password));
}

export function canResendEmailVerification(user: User | undefined) {
  const provider = user?.providerData.find((p) => p.providerId === 'password');
  return user && provider && user.email && !user.emailVerified;
}

export async function verifyEmail(code: string): Promise<Result> {
  const applied = await applyActionCode(code);
  // Reload the user to get the updated emailVerified status
  if (applied.success && _auth.currentUser) await _auth.currentUser.reload();
  return applied;
}

export async function signInEmailPassword(email: string, password: string) {
  return await toSignInResult(_firebaseAuth.signInWithEmailAndPassword(_auth, email, password));
}

export async function signInGooglePopup() {
  const provider = new _firebaseAuth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return await toSignInResult(_firebaseAuth.signInWithPopup(_auth, provider));
}

export async function verifyPasswordResetCode(code: string): Promise<VerifyPasswordResetResult> {
  try {
    const email = await _firebaseAuth.verifyPasswordResetCode(_auth, code);
    return { success: true, email };
  } catch (err) {
    console.error('auth.svelte|verifyPasswordResetCode|error', err);
    const error = parseError(err);
    return { success: false, error };
  }
}

export async function confirmPasswordReset(code: string, newPassword: string): Promise<Result> {
  try {
    await _firebaseAuth.confirmPasswordReset(_auth, code, newPassword);
    return { success: true };
  } catch (err) {
    console.error('auth.svelte|confirmPasswordReset|error', err);
    const error = parseError(err);
    return { success: false, error };
  }
}

export async function signInWithCustomToken(customToken: string): Promise<SignInResult> {
  return await toSignInResult(_firebaseAuth.signInWithCustomToken(_auth, customToken));
}

export async function changeEmail(code: string) {
  return await applyActionCode(code);
}

export async function updateProfile(user: User, displayName?: string, photoUrl?: string) {
  try {
    await _firebaseAuth.updateProfile(user, { displayName, photoURL: photoUrl });
    return { success: true };
  } catch (err) {
    console.error('auth.svelte|updateProfile|error', err);
    const error = parseError(err);
    return { success: false, error };
  }
}

export async function signOut() {
  await _firebaseAuth.signOut(_auth);
}

/**
 * Subscribe to Firebase auth state changes. Browser-only.
 * Returns an unsubscribe function, or undefined during SSR.
 */
export async function onAuthChange(callback: (user: User | null) => void): Promise<(() => void) | undefined> {
  if (!browser) return undefined;
  await _initPromise;
  return _firebaseAuth.onAuthStateChanged(_auth, callback);
}

/**
 * Get the current Firebase Auth user. Browser-only.
 * Must be called after authReady resolves.
 */
export function getCurrentUser(): User | null | undefined {
  if (!browser || !_auth) return undefined;
  return _auth.currentUser;
}

async function toSignInResult(uc: Promise<UserCredential>): Promise<SignInResult> {
  return await uc
    .then(({ user }) => ({ success: true, user }))
    .catch(({ code }) => ({ success: false, error: parseError(code) }));
}

async function applyActionCode(code: string): Promise<Result> {
  try {
    await _firebaseAuth.applyActionCode(_auth, code);
    return { success: true };
  } catch (err) {
    console.error('auth.svelte|applyActionCode|error', err);
    const error = parseError(err);
    return { success: false, error };
  }
}

/**
 * Composes a UserProfile from the Firebase User and the cached handle claim.
 * Reads name from `user.displayName` (kept in sync with the canonical name
 * by the NameChanged auth handler) and handle from the module-level cache
 * populated by `refreshHandleClaim()`.
 *
 * Returns `undefined` unless BOTH handle and name are present. Consumers
 * treat a truthy `auth.profile` as "user is fully onboarded" — returning a
 * partial profile would falsely signal readiness for users mid-onboarding
 * or with stale client state (e.g. user deleted in Firebase Console while
 * IndexedDB still holds a cached User).
 */
export function toUserProfile(user: User | undefined | null): UserProfile | undefined {
  if (!user) return undefined;
  const handle = handleCache.value;
  const handleDisplay = handleCache.display;
  const name = user.displayName?.trim() ? user.displayName : undefined;
  if (!handle || !name) return undefined;
  return { id: user.uid, handle, handleDisplay, name };
}

const errorCodeMessages = new Map([
  ['auth/too-many-requests', 'Too many requests. Please try again later.'],
  ['auth/email-already-in-use', 'The email address is already in use.'],
  ['auth/invalid-credential', 'Incorrect username or password. Please try again.'],
  ['auth/user-disabled', 'The account is disabled.'],
  ['auth/session-cookie-expired', 'The session cookie has expired.'],
  ['auth/session-cookie-revoked', 'The session cookie has been revoked.'],
  ['auth/invalid-action-code', 'The code is invalid, expired, or has already been used.'],
  ['auth/network-request-failed', 'The network request failed. Please check your internet connection and try again.'],
  ['auth/expired-action-code', 'The code has expired. Please request a new one.'],
  ['auth/user-not-found', 'The account could not be found.'],
  ['auth/custom-token-mismatch', 'The custom token does not match.'],
  ['auth/invalid-custom-token', 'The custom token is invalid.'],
  ['auth/weak-password', 'The password is too weak.'],
]);

function parseError(error: unknown) {
  const code = `${error}`.replace('FirebaseError: Firebase: Error (', '').replace(').', '');
  const message = errorCodeMessages.get(code) ?? code;
  const ecm: ErrorCodeMessage = { code, message };
  return ecm;
}
